import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen, Users, TrendingUp, AlertTriangle, ArrowRight } from "lucide-react";
import { useSubjectClasses } from "@/hooks/useSubjectClasses";
import { differenceInDays } from "date-fns";

export const ClassOverviewWidget = () => {
  const navigate = useNavigate();
  const { data: classes, isLoading } = useSubjectClasses(false);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const activeClasses = classes?.filter(c => c.status === "Active") || [];
  const teachingCompleteClasses = classes?.filter(c => c.status === "Teaching Complete") || [];
  const closedClasses = classes?.filter(c => c.status === "Closed") || [];

  // Find classes over target duration
  const overTargetClasses = activeClasses.filter(c => {
    if (!c.target_end_date) return false;
    return new Date() > new Date(c.target_end_date);
  });

  const totalStudents = classes?.reduce((acc, c) => acc + (c.students_count || 0), 0) || 0;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-primary" />
            Class Overview
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => navigate("/subject-classes")}>
            View All
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="text-center p-3 rounded-lg bg-green-500/10">
            <p className="text-2xl font-bold text-green-600">{activeClasses.length}</p>
            <p className="text-xs text-muted-foreground">Active</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-blue-500/10">
            <p className="text-2xl font-bold text-blue-600">{teachingCompleteClasses.length}</p>
            <p className="text-xs text-muted-foreground">Exams Phase</p>
          </div>
          <div className="text-center p-3 rounded-lg bg-muted">
            <p className="text-2xl font-bold">{closedClasses.length}</p>
            <p className="text-xs text-muted-foreground">Completed</p>
          </div>
        </div>

        {/* Total Students */}
        <div className="flex items-center justify-between p-3 rounded-lg border mb-4">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            <span className="text-sm">Total Students in Classes</span>
          </div>
          <span className="font-bold">{totalStudents}</span>
        </div>

        {/* Alerts */}
        {overTargetClasses.length > 0 && (
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-sm font-medium">
                {overTargetClasses.length} class{overTargetClasses.length > 1 ? "es" : ""} over target duration
              </span>
            </div>
            <div className="mt-2 space-y-1">
              {overTargetClasses.slice(0, 2).map(c => {
                const daysOver = differenceInDays(new Date(), new Date(c.target_end_date!));
                return (
                  <div key={c.id} className="text-xs text-muted-foreground">
                    {c.class_name} - {daysOver} days over
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {classes?.length === 0 && (
          <div className="text-center py-4">
            <p className="text-muted-foreground text-sm">No classes created yet</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
